// File: `typescript/apps/front-end/src/main/react/Modal/Modal.tsx`
import React from "react";
import { createRoot, Root } from "react-dom/client";
import { createPortal } from "react-dom";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import {X} from "lucide-react";
import {focusDialogOnOpen} from "@/lib/device";
import logger from "@/lib/logger";
import { LocalizationUtil } from "@/main/util/LocalizationUtil";
import {deferRootUnmount} from "./deferRootUnmount";

const PROGRAMMATIC_DIALOG_ROOT_CLEANUP_DELAY_MS = 0;

type CardDialogProps = {
    open: boolean;
    title?: string;
    onClose: () => void;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    ariaLabelledBy?: string;
    loading?: boolean;
    className?: string;
    cardClassName?: string;
    contentClassName?: string;
};

const RESPONSIVE_MODAL_SIZING =
    "w-full max-h-[calc(var(--seavien-app-viewport-height)-0.5rem)] max-w-none sm:max-h-[calc(var(--seavien-app-viewport-height)-2rem)] sm:max-w-[calc(100vw-2rem)] md:max-w-2xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-[90rem] mx-auto";

const getDialogLoadingLabel = () => LocalizationUtil.get("shared.dialog.loading");
const getDialogCloseLabel = () => LocalizationUtil.get("shared.dialog.close");

export function CardDialog({
                              open,
                              title,
                              onClose,
                              children,
                              footer,
                              ariaLabelledBy,
                              loading = false,
                              className,
                              cardClassName,
                              contentClassName,
                           }: CardDialogProps) {
    const hostRef = React.useRef<HTMLDivElement | null>(null);
    const previouslyFocused = React.useRef<Element | null>(null);

    if (!hostRef.current) {
        const el = document.createElement("div");
        el.className = "card-modal-portal";
        hostRef.current = el;
    }

    React.useEffect(() => {
        const host = hostRef.current!;
        document.body.appendChild(host);
        return () => {
            if (host.parentElement) host.parentElement.removeChild(host);
        };
    }, []);

    React.useEffect(() => {
        const host = hostRef.current!;
        if (!open) {
            return;
        }

        previouslyFocused.current = document.activeElement;
        document.body.classList.add("modal-open");

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);

        const focusTimeout = setTimeout(() => {
            focusDialogOnOpen(host);
        }, 0);

        return () => {
            clearTimeout(focusTimeout);
            document.removeEventListener("keydown", onKey);
            document.body.classList.remove("modal-open");
            if (previouslyFocused.current instanceof HTMLElement) previouslyFocused.current.focus();
        };
    }, [open, onClose]);

    const host = hostRef.current!;
    const modal = (
        <div
            className="fixed inset-0 z-layer-overlay flex items-end justify-center overflow-hidden sm:items-center sm:overflow-y-auto no-scrollbar sm:py-6"
            role="presentation"
            aria-hidden={!open}
            style={{ display: open ? undefined : "none" }}
            onMouseDown={(ev) => {
                if ((ev.target as HTMLElement)?.classList.contains("card-modal-overlay")) {
                    onClose();
                }
            }}
        >
            <div className="card-modal-overlay absolute inset-0" style={{ background: "var(--modal-overlay)" }} />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={ariaLabelledBy}
                // ⚠️ Declares openness ON THE DIALOG NODE, because the only other signal is a
                // `display:none` on the PARENT above and `display` is not inherited — so anything
                // asking "is this dialog open?" from the dialog element itself computed `block` and
                // got the wrong answer. That is not hypothetical: it swallowed Android hardware Back
                // for the life of the page after one reply-then-cancel, because `keepMounted` leaves
                // this node in the document forever (F2, 2026-09-04). Radix stamps the same attribute,
                // so consumers can select `[role="dialog"][data-state="open"]` across both families.
                // Keep it in sync with `aria-hidden` on the parent; they are the same fact.
                data-state={open ? "open" : "closed"}
                className={`relative z-layer-overlay-front w-full px-0 pt-0 pb-0 sm:px-4 ${RESPONSIVE_MODAL_SIZING} ${className ?? ""}`}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <Card className={`relative flex max-h-full w-full flex-col overflow-hidden rounded-t-[1.75rem] sm:rounded-2xl ${cardClassName ?? ""}`}>
                    {loading ? (
                        <div className="flex flex-col items-center justify-center p-8">
                            <div
                                role="status"
                                aria-live="polite"
                                aria-label={getDialogLoadingLabel()}
                                className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full mb-2"
                            />
                        </div>
                    ) : (
                        <>
                            {title && (
                                <CardHeader className="flex items-center text-center justify-between">
                                    <div id={ariaLabelledBy} className={"text-xl bold"}>{title}</div>
                                    <button aria-label={getDialogCloseLabel()} onClick={onClose} className="modal-close standard-hover focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]">
                                        ✕
                                    </button>
                                </CardHeader>
                            )}
                            <CardContent className={`min-h-0 flex-1 overflow-y-auto no-scrollbar ${contentClassName ?? ""}`}>{children}</CardContent>
                            {footer && <CardFooter className="shrink-0">{footer}</CardFooter>}
                        </>
                    )}
                </Card>

            </div>
        </div>
    );

    return createPortal(modal, host);
}

type ShowDialogOptions = {
    wrapperClassName?: string;
    modalElId?: string;
    modalElDataset?: Record<string, string>;
    addBodyModalOpen?: boolean;
    keepMounted?: boolean;
    wrapWithOverlay?: boolean; // when true, showModal will render a generic overlay + dialog container
    ariaLabelledBy?: string; // applied to dialog container when wrapWithOverlay is true
    dialogClassName?: string; // classes applied to the dialog container when wrapWithOverlay is true
};

export type DialogController = {
    cleanup: () => void;
    open: () => void;
    close: () => void;
    setOpen: (open: boolean) => void;
    isOpen: () => boolean;
};

export function showDialog(
    factory: (cleanup: () => void, setOpen: (open: boolean) => void, isOpen: boolean) => React.ReactElement,
    options: ShowDialogOptions = {}
): DialogController {
    const {
        wrapperClassName = "",
        modalElId,
        modalElDataset,
        addBodyModalOpen = true,
        keepMounted = false,
        wrapWithOverlay = false,
        ariaLabelledBy,
        dialogClassName,
    } = options;

    const wrapper = document.createElement("div");
    if (wrapperClassName) wrapper.className = wrapperClassName;

    let modalEl: HTMLDivElement | null = null;
    if (modalElId) {
        modalEl = document.createElement("div");
        modalEl.id = modalElId;
        if (modalElDataset) {
            Object.keys(modalElDataset).forEach((k) => {
                modalEl!.dataset[k] = modalElDataset[k];
            });
        }
        wrapper.appendChild(modalEl);
    }

    const container = document.createElement("div");
    (modalEl ?? wrapper).appendChild(container);
    document.body.appendChild(wrapper);

    const root: Root = createRoot(container);

    let open = true;
    let cleaned = false;
    let cleanupTimeoutId: number | null = null;

    let prevFocused: Element | null = null;
    let prevOverflow = "";
    let appRoot: Element | null = null;

    const escapeHandler = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
    };

    const outsideClickHandler = (e: MouseEvent) => {
        const target = e.target as Node | null;
        if (!target) return;
        try {
            const portals = Array.from(document.querySelectorAll(".card-modal-portal")) as Element[];
            for (const p of portals) {
                if (p.contains(target)) return;
            }
        } catch {}
        const boundary = modalEl ?? wrapper;
        if (!boundary.contains(target)) setOpen(false);
    };

    function attachGlobalListeners() {
        if (addBodyModalOpen) document.body.classList.add("modal-open");
        document.addEventListener("keydown", escapeHandler);
        document.addEventListener("mousedown", outsideClickHandler);
        if (wrapWithOverlay) {
            prevFocused = document.activeElement;
            appRoot = document.getElementById("app-root") || document.body;
            try {
                (appRoot as any).inert = true;
            } catch {}
            appRoot.setAttribute("aria-hidden", "true");
            prevOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
        }
    }

    function detachGlobalListeners() {
        document.removeEventListener("keydown", escapeHandler);
        document.removeEventListener("mousedown", outsideClickHandler);
        if (addBodyModalOpen) document.body.classList.remove("modal-open");
        if (wrapWithOverlay) {
            try {
                (appRoot as any).inert = false;
            } catch {}
            if (appRoot) appRoot.removeAttribute("aria-hidden");
            document.body.style.overflow = prevOverflow || "";
            if (prevFocused instanceof HTMLElement) prevFocused.focus();
        }
    }

    function clearScheduledCleanup() {
        if (cleanupTimeoutId === null || typeof window === "undefined") {
            return;
        }

        window.clearTimeout(cleanupTimeoutId);
        cleanupTimeoutId = null;
    }

    function renderCurrent() {
        try {
            const inner = factory(cleanup, setOpen, open);
            if (wrapWithOverlay) {
                const wrapped = (
                    <div
                        className="fixed inset-0 z-layer-overlay flex items-center justify-center"
                        role="presentation"
                        aria-hidden={!open}
                        style={{ display: open ? undefined : "none" }}
                    >
                        <div
                            className="absolute inset-0 backdrop-blur-sm"
                            style={{ background: "var(--modal-overlay-strong)" }}
                            onClick={() => setOpen(false)}
                        />
                        <div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={ariaLabelledBy}
                            // Same contract as `CardDialog` above — see the comment there. This branch
                            // is the one `showThoughtTextEditorDialog` reaches, and it defaults
                            // `keepMounted = true`, so it is where F2 actually bit.
                            data-state={open ? "open" : "closed"}
                            className={`relative z-layer-overlay-front flex items-center justify-center p-6 ${RESPONSIVE_MODAL_SIZING} ${dialogClassName ?? ""}`}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            {inner}
                        </div>
                        <button
                            aria-label={getDialogCloseLabel()}
                            onClick={() => setOpen(false)}
                            className="absolute top-4 right-4 z-layer-overlay-front inline-flex items-center justify-center rounded-full standard-hover focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                        >
                            <X size={30}   />
                        </button>
                    </div>
                );
                root.render(wrapped);
            } else {
                root.render(inner);
            }
        } catch (e) {
            logger.error("[showDialog] render error", e);
        }
    }

    function setOpen(value: boolean) {
        if (cleaned) return;
        open = value;
        if (open) {
            clearScheduledCleanup();
            attachGlobalListeners();
            renderCurrent();
            if (wrapWithOverlay) {
                // focus into the dialog (skipping editable fields on touch devices)
                setTimeout(() => {
                    try {
                        focusDialogOnOpen(container);
                    } catch {}
                }, 0);
            }
        } else {
            detachGlobalListeners();
            if (keepMounted) {
                renderCurrent();
            } else {
                scheduleCleanup();
            }
        }
    }

    function scheduleCleanup() {
        if (cleaned || cleanupTimeoutId !== null) return;

        if (typeof window === "undefined") {
            cleanup();
            return;
        }

        cleanupTimeoutId = window.setTimeout(() => {
            cleanupTimeoutId = null;
            cleanup();
        }, PROGRAMMATIC_DIALOG_ROOT_CLEANUP_DELAY_MS);
    }

    function cleanup() {
        if (cleaned) return;
        cleaned = true;
        clearScheduledCleanup();
        // Listeners must go NOW: a late key or click handler acting on a logically-closed dialog is
        // its own bug. The root unmount is deferred one macrotask, because every caller reaches here
        // from a useEffect teardown and React runs those mid-commit. See deferRootUnmount.
        detachGlobalListeners();
        deferRootUnmount(root, wrapper);
    }

    // initial mount
    attachGlobalListeners();
    renderCurrent();
    if (open && wrapWithOverlay) {
        setTimeout(() => {
            try {
                focusDialogOnOpen(container);
            } catch {}
        }, 0);
    }

    return {
        cleanup,
        open: () => setOpen(true),
        close: () => setOpen(false),
        setOpen,
        isOpen: () => open,
    };
}
